import React, { useEffect, useState } from "react";
import { MainLayout } from "components/layout/MainLayout";
import { Card, CardHeader, CardBody, Button, Modal } from "components/ui";
import { useToast } from "hooks/useToast";
import { IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "constants/index";
import {
  AddOutlined,
  VisibilityOutlined,
  EditOutlined,
  DeleteOutline,
} from "@mui/icons-material";
import { contactUsService } from "services/contactus.service";
import { Package } from "types/package";
import parse from "html-react-parser";
import { ContactUs } from "@/types/contactus";

export const ContactUsPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [page, setPage] = useState(1);
  const pageSize = 100;
  const [rows, setRows] = useState<ContactUs[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pagedRows = rows;

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await contactUsService.list(page, pageSize);
        setRows(res.data);
        setTotal(res.pagination.total);
      } catch (e: any) {
        setError(e?.message || "Failed to load packages");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [page]);

  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages, p + 1));


  const onView = (id: string) => {
    navigate(`${ROUTES.CONTACTUS}/${id}/view`);
  };


  return (
    <MainLayout>
      <Card>
        <CardHeader title="Inbox" />
        <CardBody>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
            <div className="text-sm text-secondary-600">
              Page {page} of {totalPages}
            </div>
          </div>

          {error && <div className="mb-3 text-sm text-red-600">{error}</div>}
          {isLoading && (
            <div className="mb-3 text-sm text-secondary-600">Loading...</div>
          )}

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {!isLoading && !error && pagedRows.length === 0 && (
              <div className="p-6 text-center text-secondary-600">No data</div>
            )}
            {pagedRows.map((msg) => (
              <div
                key={msg.id}
                className="bg-white border border-secondary-200 rounded-lg p-4 space-y-3"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-secondary-900 text-base">
                      {msg.name}
                    </h3>
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onView(msg.id)}
                      className="p-2"
                    >
                      <VisibilityOutlined fontSize="small" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-secondary-500 font-medium min-w-[140px]">
                      Name:
                    </span>
                    <span className="text-secondary-900">{msg.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-secondary-500 font-medium min-w-[140px]">
                      Email:
                    </span>
                    <span className="text-secondary-900">{msg.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-secondary-500 font-medium min-w-[140px]">
                      Company Name:
                    </span>
                    <span className="text-secondary-900">
                      {msg.companyName}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-secondary-500 font-medium min-w-[140px]">
                      Date:
                    </span>
                    <span className="text-secondary-900">
                      {msg.date}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border border-secondary-200 rounded-md overflow-hidden">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Name
                  </th>
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Email
                  </th>
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Company Name
                  </th>
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Date
                  </th>
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {!isLoading && !error && pagedRows.length === 0 && (
                  <tr>
                    <td
                      className="p-6 text-center text-secondary-600"
                      colSpan={5}
                    >
                      No data
                    </td>
                  </tr>
                )}
                {pagedRows.map((msg) => (
                  <tr
                    key={msg.id}
                    className="border-t border-secondary-100 hover:bg-secondary-50"
                  >
                    <td className="p-3">{msg.name}</td>
                    <td className="p-3">{msg.email}</td>
                    <td className="p-3">
                      {msg.companyName}
                    </td>
                    <td className="p-3">
                      {msg.date}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <IconButton
                          size="small"
                          onClick={() => onView(msg.id)}
                          aria-label="View"
                        >
                          <VisibilityOutlined fontSize="small" />
                        </IconButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={goPrev}
              disabled={page === 1}
              className="w-full sm:w-auto"
            >
              Prev
            </Button>
            <div className="text-sm text-secondary-600 text-center">
              {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of{" "}
              {total}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={goNext}
              disabled={page === totalPages}
              className="w-full sm:w-auto"
            >
              Next
            </Button>
          </div>
        </CardBody>
      </Card>
    </MainLayout>
  );
};
